// Sections 6, 8 and 15: consecutive-season counters that the Section 23 formulas key on
// (hungry 2+, dehydrated 2+, cold 2+, exposed in Winter), plus stunting and idleness. Computed
// from the committed person and this season's needs; the draft is written only on change.
import { isChild, isWorking } from '../../core/queries';
import type { NeedSatisfaction, Person, PhaseContext, Streaks } from '../../core/types';
import { seal } from '../shared/seal';

function nextStreaks(ctx: PhaseContext, p: Person, n: NeedSatisfaction): Streaks {
  const cfg = ctx.cfg;
  const short = cfg.needs.shortThreshold;
  const s = p.streaks;
  const warmthNeeded = cfg.needs.warmthPerBed[ctx.season.season] > 0;
  const child = isChild(cfg, p);
  return {
    hungry: n.food < short ? s.hungry + 1 : 0,
    thirsty: n.water < short ? s.thirsty + 1 : 0,
    cold: warmthNeeded && n.warmth < short ? s.cold + 1 : 0,
    exposed: n.shelter < short ? s.exposed + 1 : 0,
    fed: n.food >= cfg.needs.metThreshold ? s.fed + 1 : 0,
    childhoodHungry: child && n.food < short ? s.childhoodHungry + 1 : s.childhoodHungry,
    emigration: s.emigration,
    idle: !child && p.job === 'rest' ? s.idle + 1 : 0,
    inJob: isWorking(p) ? s.inJob + 1 : s.inJob,
    school: s.school,
  };
}

function same(a: Streaks, b: Streaks): boolean {
  for (const key of Object.keys(a) as (keyof Streaks)[]) if (a[key] !== b[key]) return false;
  return true;
}

export function updateStreaks(ctx: PhaseContext, p: Person, needs: NeedSatisfaction): void {
  const next = nextStreaks(ctx, p, needs);
  const stunt =
    isChild(ctx.cfg, p) &&
    !p.status.stunted &&
    next.childhoodHungry >= ctx.cfg.needs.stuntedAfterSeasons;
  if (same(p.streaks, next) && !stunt) return;
  const target = ctx.draft.people[p.id];
  if (target === undefined) return;
  target.streaks = seal(ctx, next);
  if (stunt) {
    target.status.stunted = true;
    ctx.report.add({ kind: 'stunted', personId: p.id });
  }
}
